import React, {useState, useContext, Fragment} from 'react';
import moment from "moment";
import __ from "lodash";
import {
  FormControl,
  FormGroup,
  Grid,
} from "@material-ui/core";
import Divider from '@mui/material/Divider';
import SystemContext from "../../../SystemContext";
import {formatChars} from "../../../blocks/FormatCharacters";
import {
  ComboSelect,
  DatePicker,
  FormDialog, FormSelect,
  LabelledPaper, MaskedTime, MaskedText, Text, CheckboxEditor, MaskedNumber,
} from '../../../controls';
import RequiredDays from "./RequiredDays";
import CostItemValidator from "../validators/AirportCostItemValidator";
import CurrencyLookup from "../../Currencies/components/CurrencyLookup";
import {CostCategoryUuidLookup} from "../../Costings/components/CostCategoryUuidLookup";
import CompanyUuidLookup from "../../Companies/components/CompanyUuidLookup";
import {useAddAirportCost, useUpdateAirportCost} from "../../../services/airports";

const dateFormat = "DD/MM/YYYY";
const directions = [
  {code: null, value: "(None)"},  
  {code: "OUT", value: "Outbound"},
  {code: "IN", value: "Inbound"},
];

const cargoHandlingTypes = [
  {code: "NONE", value: "None"},
  {code: "BOTH", value: "Both"},
  {code: "LOADING", value: "Loading"},
  {code: "UNLOADING", value: "Unloading"},
  {code: "LOADING ONLY", value: "Loading Only"},
  {code: "UNLOADING ONLY", value: "Unloading Only"},
  {code: "ANY", value: "Any"},
];

const paperProps = {
  style: {
    backgroundColor: "#f8f8f8",
    boxShadow: 'none',
  }
}

const defaultLabelledPaperProps = {
  checkChildren: true,
  style: {display: "block", border: "solid 1px #b9b9b9" },
  labelStyle: {background: "#f0f0f0"},
  collapsible: false,
}


const AirportCostDialog = (_props) => {
  const props = {
    canEdit: false,
    ..._props,
  }

  const SystemData = useContext(SystemContext);
  const classes = props.classes;
  const [dialogStates, setDialogStates] = useState(props.dialogStates);
  const [originalSlotRecord] = useState(props.record?.parentRecord||props.record);
  const [costItemRecord, setCostItemRecord] = useState({
    ...props.record, cargoHandlingType: props.record.cargoHandlingType ?? "IGNORE"
  });
  const [editing, setEditing] = useState(false);
  const [newCostItem, setNewCostItem] = useState(props.newCostItem || false);

  const [alert, setAlert] = useState({
    open: false,
    severity: "error",
    message: "",
  });

  const handleAddAirportCost = useAddAirportCost();
  const handleUpdateAirportCost = useUpdateAirportCost();
  const extraKeys = [];

  const getButtons = () => {
    if (props.canEdit) {
      if (newCostItem) {
        return (
          {
            save:{icon: "check_circle", key: "Save", label: "Save", color: "primary", disabled: (status)=>{return status.error}},
            cancel:{icon: "cancel", key: "Cancel", label: "Cancel", color: "primary"}
          }
        );
      }
      if (editing) {
        return (
          {
            save:{icon: "check_circle", key: "Save", label: "Save", color: "primary", disabled: (status)=>{return status.error}},
            cancel:{icon: "cancel", key: "Cancel", label: "Cancel", color: "primary"},
          }
        );
      }
      return (
        {
          edit: {icon: "edit", key: "Edit", label: "Edit", color: "primary"},
          cancel:{icon: "cancel", key: "Cancel", label: "Cancel", color: "primary"}
        }
      )
    }
    return (
      {
        cancel:{icon: "cancel", key: "Cancel", label: "Close", color: "primary"}
      }
    );
  }

  return (
    <div key="formContainer" style={{width: "100%", textAlign:"left" }}>
      {costItemRecord
        ?
          <FormDialog
            key="airport cost editor"
            title={`${newCostItem?"Add New Airport Cost Item" :"Update Airport Cost Item"}${costItemRecord?.airportCode?" for " + costItemRecord.airportCode:""}`}
            message=""
            open={dialogStates.showDialog}
            dialogOptions={{maxWidth: "md", fullWidth: true, PaperProps: paperProps }}
            record={{...costItemRecord}}
            validator={(newCostItem || editing)?CostItemValidator:undefined}
            validatorPosition="top"
            alert={alert}
            formHeightOffset={320}
            titleBarStyle={{backgroundColor: '#3f3f3f', color: 'primary'}}
            buttons={getButtons()}
            movable
            onChange={(state) => {
              if (state.buttonPressed === "edit" && props.canEdit) {
                setEditing(true);
              }
              if (state.buttonPressed === "cancel") {
                setEditing(false);
                setCostItemRecord(props.record);
                setDialogStates({showDialog: false});
                if (props.close)
                  props.close();
              }
              if (state.buttonPressed === "save") {
                SystemData.toggleLoadingProgress(true);
                if (newCostItem) {
                  const newRecord = __.clone(state.record);
                  newRecord["airportCostUuid"] = undefined;
                  newRecord["displayOrder"] = 999;
                  newRecord["cargoHandlingType"] = newRecord.cargoHandlingType === "IGNORE" ? null : newRecord.cargoHandlingType;

                  handleAddAirportCost.mutate({
                    extraKeys,
                    record: newRecord,
                    postSuccess: (result) => {
                      setEditing(false);
                      setNewCostItem(false);
                      setAlert({ severity: "info", open: true, message: `New Airport Cost Item saved`, timeOut: 5 });
                      setDialogStates({showDialog: false});
                      if (props.close)
                        props.close();

                      SystemData.toggleLoadingProgress(false);
                    },
                    postError: (data) => {
                      SystemData.toggleLoadingProgress(false);
                      if (data.errorMessage) {
                        SystemData.alert("Error", `${data.errorMessage}`);
                      } else {
                        SystemData.alert("Error", `An Unexpected Error has occurred saving the airport cost item`);
                      }
                    }
                  });
                } else {
                  const updatedRecord = state.record;
                  updatedRecord["updatedAt"] = moment().format("YYYY-MM-DD");
                  handleUpdateAirportCost.mutate({
                    extraKeys,
                    record: updatedRecord,
                    postSuccess: (result) => {
                      setEditing(false);
                      setNewCostItem(false);
                      setAlert({ severity: "info", open: true, message: `Airport Cost Item updated`, timeOut: 5 });
                      setDialogStates({showDialog: false});
                      if (props.close)
                        props.close();

                      SystemData.toggleLoadingProgress(false);
                    },
                    postError: (data) => {
                      SystemData.toggleLoadingProgress(false);
                      if (data.errorMessage) {
                        SystemData.alert("Error", `${data.errorMessage}`);
                      } else {
                        SystemData.alert("Error", `An Unexpected Error has occurred updating the airport cost item`);
                      }
                    },
                    postSettled: (result) =>{
                      setEditing(false);
                      setNewCostItem(false);
                      setDialogStates({showDialog: false});
                      if (props.close)
                        props.close();
                      }
                  });

                  SystemData.toggleLoadingProgress(false);
                }
              } else {
                console.log("Unhandled FormDialog onChange");
              }
            }}
          >
            <Grid container checkChildren={true} spacing={3}>
              <Grid item xs={12} checkChildren={true} row>
                <LabelledPaper {...defaultLabelledPaperProps} label="Cost Item Details" >
                  <FormControl checkChildren={true} key="FC-costitemdetails" style={{display: "block" }}>
                    <FormGroup checkChildren={true} key="FG-costitemdetails" style={{padding: "8px 8px 8px 8px"}}>
                      <Grid container checkChildren={true} spacing={3}>

                      <Grid item xs={3} checkchildren="true">
                          {record => (
                            <ComboSelect
                              id="aircraftType"
                              label="Aircraft Type"
                              inputLabelProps={{ shrink: true }}
                              group="AIRCRAFT TYPES"
                              subGroup=""
                              fullWidth
                              value={props.value || ""}
                              readOnly={!newCostItem && editing !== true}
                              onChange={props.onChange}
                            />
                          )}
                        </Grid>

                        <Grid item xs={2} checkchildren="true">
                          {record => (
                            <ComboSelect
                              id="airportVariant"
                              label="Variant"
                              inputLabelProps={{ shrink: true }}
                              group="AIRPORT VARIANTS"
                              subGroup="AIRLINE"
                              fullWidth
                              value={props.value || ""}
                              readOnly={!newCostItem && editing !== true}
                              onChange={props.onChange}
                            />
                          )}
                        </Grid>

                        <Grid item xs={7} checkchildren="true">
                          {record => (
                            <MaskedText
                              id="costDescription"
                              label="Description"
                              inputLabelProps={{ shrink: true }}
                              mask={"x".repeat(200)}
                              maskChar={""}
                              alwaysShowMask={false}
                              formatChars={formatChars}
                              readOnly={!newCostItem && editing !== true}
                              onChange={props.onChange}
                            />
                          )}
                        </Grid>

                        <Grid item xs={12} />

                        <Grid item xs={4} checkchildren="true">
                          {record => (
                            <CostCategoryUuidLookup
                              id="costCategoryUuid"
                              label="Cost Category"
                              shrink={true}
                              readOnly={!newCostItem && editing !== true}
                              returnType={"object"}
                              onChange={(returnObject, props) => {
                                if (returnObject.found) {
                                  const returnedData = returnObject.data;
                                  const data = {
                                    ...props.record,
                                    costCategoryName: returnedData.costCategoryName,
                                    costCategoryUuid: returnedData.costCategoryUuid,
                                    ledgerCode: returnedData.ledgerCode,
                                  }
                                  props.recordChange(data);
                                } else {
                                  props.onChange(returnObject.value);
                                }
                              }}
                            />
                          )}
                        </Grid>
                        <Grid item xs={4} checkchildren="true">
                          {record => (
                            <CompanyUuidLookup
                              id="companyUuid"
                              label="Supplier"
                              shrink={true}
                              readOnly={!newCostItem && editing !== true}
                              companyType={"SUPPLIER"}
                              value={props.value}
                              alwaysShowCompanyName={true}
                              hideCode={true}
                              returnType={"object"}
                              onChange={(returnObject, props) => {
                                if (returnObject.found) {
                                  const returnedData = returnObject.data
                                  const data = {
                                    ...props.record,
                                    supplierName: returnedData.companyName,
                                    companyUuid: returnedData.companyUuid,
                                    currencyCode: returnedData.defaultCurrencyCode ?? props?.record?.currencyCode
                                  }
                                  props.recordChange(data);
                                } else {
                                  props.onChange(returnObject.value);
                                }
                              }}
                            />
                          )}
                        </Grid>

                        <Grid item xs={4} checkchildren="true">
                          {record => (
                            <FormSelect
                              id="direction"
                              label="Direction"
                              options={directions}
                              readOnly={!newCostItem && editing !== true}
                              style={{marginTop: "3px"}}
                              fullWidth
                              onChange={(value, props) => {
                                props.recordChange({...props.record, direction: value, validFrom: null, validTo: null, validTimeFrom: '00:00', validTimeTo: '23:59', days: "1234567"});
                              }}
                            />
                          )}
                        </Grid>

                        <Grid item xs={12} >
                          <Divider variant={"fullWidth"} textAlign="center"></Divider>
                        </Grid>
                        <Grid item xs={12}/>

                        <Grid item xs={1} checkchildren="true">
                          {record => (
                            <CheckboxEditor
                              id="active"
                              label="Active"
                              readOnly={!newCostItem && editing !== true}
                              onChange={(value, props) => {
                                if (value === false) {
                                  props.recordChange({...props.record, active: false})
                                } else {
                                  props.recordChange({...props.record, active: true})}
                              }}
                            />)
                          }
                        </Grid>
                        <Grid item xs={2} checkchildren="true">
                          {record => {
                            if (!record.direction || record.direction === "N/A") {
                              return (
                                <Fragment checkChildren={false}>
                                  <Text id="validFrom" label="Valid From" readOnly={true} value="N/A"/>
                                </Fragment>
                              )
                            } else {
                              return (
                                <DatePicker
                                  id="validFrom"
                                  label="Valid From"
                                  disableToolbar={true}
                                  autoOk={true}
                                  format={dateFormat}
                                  readOnly={(!newCostItem && editing !== true)}
                                />
                              )
                            }

                          }}
                        </Grid>
                        <Grid item xs={2} checkchildren="true">
                          {record => {
                            if (!record.direction || record.direction === "N/A") {
                              return (
                                <Fragment checkChildren={false}>
                                  <Text id="validTo" label="Valid To" readOnly={true} value="N/A"/>
                                </Fragment>
                              )
                            } else {
                              return (
                                <DatePicker
                                  id="validTo"
                                  label="Valid To"
                                  minDate={record.validFrom}
                                  disableToolbar={true}
                                  autoOk={true}
                                  format={dateFormat}
                                  readOnly={!newCostItem && editing !== true}
                                />
                              )
                            }
                          }}
                        </Grid>

                        <Grid item xs={2} checkchildren="true">
                          {record => {
                            if (!record.direction || record.direction === "N/A") {
                              return (
                                <Fragment checkChildren={false}>
                                  <Text id="validTimeFrom" label="From Hour" readOnly={true} value="N/A"/>
                                </Fragment>
                              )
                            } else {
                              return (
                                <MaskedTime
                                  id="validTimeFrom"
                                  label="From Hour"
                                  defaultValue={"00:00"}
                                  inputLabelProps={{shrink: true}}
                                  readOnly={!newCostItem && editing!==true}
                                />
                              )
                            }
                          }}
                        </Grid>
                        <Grid item xs={2} checkchildren="true">
                          {record => {
                            if (!record.direction || record.direction === "N/A") {
                              return (
                                <Fragment checkChildren={false}>
                                  <Text id="validTimeTo" label="To Hour" readOnly={true} value="N/A"/>
                                </Fragment>
                              )
                            } else {
                              return (
                                <MaskedTime
                                  id="validTimeTo"
                                  label="To Hour"
                                  defaultValue={"23:59"}
                                  inputLabelProps={{shrink: true}}
                                  readOnly={!newCostItem && editing!==true}
                                />
                              )
                            }
                          }}
                        </Grid>

                        <Grid item xs={3} checkchildren="true">
                          {record=>
                            <RequiredDays
                              id="days"
                              label="Days Effective"
                              readOnly={!record.direction || record.direction === "N/A"}
                              disabled={!record.direction || record.direction === "N/A"}
                              days={(!record.direction || record.direction === "N/A") ? "1234567":record.days ?? "1234567"}
                              onChange={(updatedDays, props) => {
                                if (newCostItem || editing===true) {
                                  const data = {
                                    ...props.record,
                                    days: updatedDays,
                                  }
                                  props.recordChange(data);
                                }
                              }}
                            />
                          }
                        </Grid>

                        <Grid item xs={12} >
                          <Divider variant={"fullWidth"} textAlign="center"></Divider>
                        </Grid>
                        <Grid item xs={12}/>

                        <Grid item xs={3} checkchildren="true">
                          {record => (
                            <ComboSelect
                              id="cargoHandlingType"
                              label="Handling Type"
                              inputLabelProps={{ shrink: true }}
                              group="COSTING CARGO HANDLING TYPES"
                              subGroup=""
                              fullWidth
                              readOnly={!newCostItem && editing !== true}
                              onChange={(newValue, props) => {
                                const data = {
                                  ...props.rowData,
                                  cargoHandlingType: newValue
                                }
                                props.recordChange(data);
                              }}
                            />
                          )}
                        </Grid>
                        <Grid item xs={4} checkchildren="true">
                          {record => (
                            <ComboSelect
                              id="calculationType"
                              label="Calculation Type"
                              inputLabelProps={{ shrink: true }}
                              group="COST CALCULATION TYPES"
                              subGroup=""
                              fullWidth
                              readOnly={!newCostItem && editing !== true}
                              onChange={(newValue, props) => {
                                const currentValue = props.value;
                                const data = {
                                  ...props.rowData,
                                  calculationType: newValue
                                }
                                if (newValue !== currentValue) {
                                  data["calculationUnitType"] = null;
                                }
                                props.recordChange(data);
                              }}
                            />
                          )}
                        </Grid>
                        <Grid item xs={5} checkchildren="true">
                          {record => (
                            <ComboSelect
                              id="calculationUnitType"
                              label="Calculation Unit Type"
                              inputLabelProps={{ shrink: true }}
                              group="CALCULATION UNIT TYPES"
                              subGroup={record?.calculationType}
                              fullWidth
                              readOnly={!newCostItem && editing !== true}
                            />
                          )}
                        </Grid>

                        <Grid item xs={12} >
                          <Divider variant={"fullWidth"} textAlign="center"></Divider>
                        </Grid>
                        <Grid item xs={12}/>

                        <Grid item xs={2} checkChildren={true}>
                          {record => (
                            <MaskedNumber
                              id="price"
                              label={`${record.calculationType === "PERCENTAGE_OF_COST" ? "Percentage Value" : "Price"}`}
                              inputLabelProps={{ shrink: true }}
                              decimalScale={record.calculationType === "PERCENTAGE_OF_COST" ? 2 : 4}
                              fixedDecimalScale={true}
                              suffix={`${record.calculationType === "PERCENTAGE_OF_COST" ? "%" : ""}`}
                              fullWidth
                              readOnly={!newCostItem && editing !== true}
                            />
                          )}
                        </Grid>
                        <Grid item xs={2} checkChildren={true}>
                          {record => (
                            <CurrencyLookup
                              id="currencyCode"
                              label="Currency"
                              inputLabelProps={{ shrink: true }}
                              showChildCurrencies={false}
                              readOnly={!newCostItem && editing !== true}
                            />
                          )}
                        </Grid>
                        <Grid item xs={2} checkChildren={true}>
                          {record => (
                            <MaskedNumber
                              id="minimumQuantity"
                              label="Minimum Quantity"
                              inputLabelProps={{ shrink: true }}
                              decimalScale={0}
                              defaultValue={0}
                              fixedDecimalScale={true}
                              fullWidth
                              readOnly={!newCostItem && editing !== true}
                            />
                          )}
                        </Grid>
                        <Grid item xs={2} checkChildren={true}>
                          {record => (
                            <MaskedNumber
                              id="freeQuantity"
                              label="Free Quantity"
                              inputLabelProps={{ shrink: true }}
                              decimalScale={0}
                              fixedDecimalScale={true}
                              defaultValue={0}
                              fullWidth
                              readOnly={!newCostItem && editing !== true}
                            />
                          )}
                        </Grid>
                        <Grid item xs={2} checkChildren={true}>
                          {record => (
                            <MaskedNumber
                              id="costAllocationInboundPercentage"
                              label="Inbound %"
                              inputLabelProps={{ shrink: true }}
                              format={"#".repeat(3)}
                              decimalScale={0}
                              valueCap={100}
                              suffix="%"
                              readOnly={!newCostItem && editing !== true}
                              onChange={(value, props) => {
                                const outbound = 100 - value;
                                const data = {
                                  ...props.rowData,
                                  costAllocationInboundPercentage: value,
                                  costAllocationOutboundPercentage: outbound,
                                }
                                props.recordChange(data);
                              }}
                            />
                          )}
                        </Grid>
                        <Grid item xs={2} checkChildren={true}>
                          {record => (
                            <MaskedNumber
                              id="costAllocationOutboundPercentage"
                              label="Outbound %"
                              inputLabelProps={{ shrink: true }}
                              format={"#".repeat(3)}
                              decimalScale={0}
                              valueCap={100}
                              suffix="%"
                              readOnly={!newCostItem && editing !== true}
                              onChange={(value, props) => {
                                const inbound = 100 - value;
                                const data = {
                                  ...props.rowData,
                                  costAllocationInboundPercentage: inbound,
                                  costAllocationOutboundPercentage: value,
                                }
                                props.recordChange(data);
                              }}
                            />
                          )}
                        </Grid>

                        <Grid item xs={12} >
                          <Divider variant={"fullWidth"} textAlign="center"></Divider>
                        </Grid>
                        <Grid item xs={12}/>

                        <Grid item xs={12} checkChildren={true}>
                          {record => (
                            <MaskedText
                              id="notes"
                              label="Notes"
                              inputLabelProps={{ shrink: true }}
                              style={{width: "100%"}}
                              mask={"x".repeat(250)}
                              maskChar={""}
                              alwaysShowMask={false}
                              formatChars={formatChars}
                              forceUpperCase={true}
                              readOnly={!newCostItem && editing!==true}
                            />
                          )}
                        </Grid>

                      </Grid>
                    </FormGroup>
                  </FormControl>
                </LabelledPaper>
              </Grid>

            </Grid>
          </FormDialog>
        :
          <></>
      }
    </div>
  );
}

export default AirportCostDialog;
